/* ------------------------------------------------------------------
   QrCodeCard.js  —  Link in Bio › Links › Your QR code
   (Part E, T32; BioExtras.dc.html). The code points at the live page
   with ?src=qr, so Link in Bio › Analytics can count QR visits.
   Made in the browser (./qr.js); PNG for screens, SVG for print.
------------------------------------------------------------------ */
import React, { useMemo, useState } from 'react';
import { qrPageUrl } from '../../bio/bioContact';
import { qrMatrix, qrPath, qrPngBlob, qrSvgString, saveBlob, QUIET_ZONE } from './qr';

export default function QrCodeCard({ username, onError }) {
  const [busy, setBusy] = useState(false);
  const url = username ? qrPageUrl(username) : null;
  const svg = useMemo(() => {
    if (!url) return null;
    const m = qrMatrix(url);
    return { n: m.size + QUIET_ZONE * 2, d: qrPath(m, QUIET_ZONE) };
  }, [url]);

  const base = `greatlysocial-${username || 'page'}-qr`;
  async function downloadPng() {
    setBusy(true);
    try { saveBlob(await qrPngBlob(url), `${base}.png`); }
    catch { onError && onError('Could not make the PNG. Try the SVG instead.'); }
    finally { setBusy(false); }
  }
  function downloadSvg() {
    saveBlob(new Blob([qrSvgString(url)], { type: 'image/svg+xml' }), `${base}.svg`);
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', gap: 14, alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {svg ? (
        <svg width="120" height="120" viewBox={`0 0 ${svg.n} ${svg.n}`} role="img" aria-label="QR code for your link in bio page"
          data-qr-text={url} shapeRendering="crispEdges"
          style={{ flex: 'none', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
          <rect width={svg.n} height={svg.n} fill="#ffffff" />
          <path d={svg.d} fill="#111827" />
        </svg>
      ) : (
        <div aria-hidden="true" style={{ width: 120, height: 120, flex: 'none', border: '1px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
          <i className="fa-solid fa-qrcode" style={{ fontSize: 40 }} />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Your QR code</span>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {url ? 'For flyers, business cards, open-house signs and shop windows.' : 'Save your page to get your QR code.'}
        </span>
        {url && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={downloadPng} disabled={busy}
              style={{ padding: '7px 12px', border: 0, background: '#2563eb', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
              Download PNG
            </button>
            <button type="button" onClick={downloadSvg}
              style={{ padding: '7px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
              SVG for print
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
